import { Result } from "src/utils/Result"
import { createAccountDevice, GqlError } from "src/aws/customAPI"
import { DevicePushToken } from "expo-notifications"
import * as Notifications from 'expo-notifications';
import { Platform } from "react-native"
import { DeviceKind } from "src/aws/API"
import { execApi } from "src/apis/execApi"
import { appInfo } from "src/constants/AppInfo"

type CreateDeviceSuccessResponse = {
  message: string
}

type CreateDeviceFailureResponse = {
  message: string
}

const saveDevice = (access_token: string, device_token: DevicePushToken): Promise<Result<CreateDeviceSuccessResponse, CreateDeviceFailureResponse>> => {

  if (!device_token) {
    return Promise.resolve(Result.Ok<CreateDeviceSuccessResponse>({
      message: 'No DeviceToken',
    }))
  }

  const kind = (Platform.OS === DeviceKind.ios) ? DeviceKind.ios : DeviceKind.android;

  return execApi(
    () => createAccountDevice(access_token, {
      input: {
        kind: kind,
        deviceToken: device_token.data,
      },
      appInfo
    }),
    _ => ({message: "OK"}),
    {
      identifiers: ["saveDevice", "createAccountDevice"],
    }
  )
}

const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000
): Promise<T> => {
  let lastError: any;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt < maxRetries - 1) {
        const delay = initialDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError;
}

export const registerDevice = async (access_token: string): Promise<Result<CreateDeviceSuccessResponse, CreateDeviceFailureResponse>> => {
  
  if (Platform.OS !== "ios" && Platform.OS !== "android") {
    return Result.Error<CreateDeviceFailureResponse>({
      message: 'no device'
    });
  }
  
  
  if (Platform.OS === "ios" || Platform.OS === "android") {
    const { status: existingStatus } = await Notifications.getPermissionsAsync();
    let finalStatus = existingStatus;
    if (existingStatus !== 'granted') {
      const { status } = await Notifications.requestPermissionsAsync();
      finalStatus = status;
    }
    if (finalStatus !== 'granted') {
      return Result.Error<CreateDeviceFailureResponse>({
        message: 'Failed to get push token for push notification!'
      });
    }
    
    let device_token;
    try {
      device_token = await retryWithBackoff(
        () => Notifications.getDevicePushTokenAsync(),
        3,
        1000
      );
    } catch (error) {
      console.error('[RegisterDevice] Failed to get device token after retries:', error)
      return Result.Error<CreateDeviceFailureResponse>({
        message: 'Failed to get device token after multiple attempts'
      });
    }

    if (Platform.OS === 'android') {
      await Notifications.setNotificationChannelAsync('default', {
        name: 'default',
        importance: Notifications.AndroidImportance.MAX,
        vibrationPattern: [0, 250, 250, 250],
        lightColor: '#FF231F7C',
      });
    }

    try {
      const result = await retryWithBackoff(
        () => saveDevice(access_token, device_token),
        3,
        1000
      );
      return result;
    } catch (error) {
      console.error('[RegisterDevice] Failed to register with server after retries:', error)
      return Result.Error<CreateDeviceFailureResponse>({
        message: 'Failed to register device with server'
      });
    }
  }

  return Result.Error<CreateDeviceFailureResponse>({
    message: 'Unexpected error'
  });
}
